import { useState } from 'react';
import { useRootScope } from 'FrontRoyalAngular';
import { useErrorLogService } from 'ErrorLogging';
import { buildAuthHeaders } from 'BackRoyalApi/queryShared';
import { type CameraSource, requestPermissions, getPhoto } from 'MobileFileUpload';

type UploadAvatarResponse = { contents?: { avatar?: { url?: string } } };

// Matches the old directive's `ngf-max-size="3MB"` and the codebase's maxSizeMBValidator convention
// (MB = 1024 * 1024).
export const AVATAR_MAX_SIZE_MB = 3;
const AVATAR_MAX_SIZE_BYTES = AVATAR_MAX_SIZE_MB * 1024 * 1024;

// Upload state + handlers for the account-page avatar uploader. The upload endpoint persists
// avatar_url server-side; we also copy the new url onto the shared currentUser (and its embedded
// career profile) so the rest of the app reflects it immediately — mirroring the old directive
// (see https://trello.com/c/lJmT3mWR).
export function useAvatarUpload() {
    const $rootScope = useRootScope();
    const ErrorLogService = useErrorLogService();
    const currentUser = $rootScope.currentUser;

    const [avatarUrl, setAvatarUrl] = useState(() => currentUser?.avatar_url ?? null);
    const [loading, setLoading] = useState(false);
    const [showAccessTip, setShowAccessTip] = useState(false);
    // True when the last selected file exceeded the size limit; drives the red "max size" cue,
    // mirroring the old directive's `.max-size.error` state.
    const [oversized, setOversized] = useState(false);

    const uploadUrl = `${window.ENDPOINT_ROOT}/api/users/${currentUser?.id}/upload_avatar.json`;

    const setNewValueOnModel = (url: string) => {
        setAvatarUrl(url);
        if (currentUser) {
            currentUser.avatar_url = url;
            if (currentUser.career_profile) currentUser.career_profile.avatar_url = url;
            $rootScope.$applyAsync();
        }
    };

    // Web upload: multipart form data from a file input. Files over the size limit are rejected
    // client-side (showing the red "max size" cue) rather than uploaded, matching the old directive.
    const uploadFile = async (file: File) => {
        if (file.size > AVATAR_MAX_SIZE_BYTES) {
            setOversized(true);
            return;
        }
        setOversized(false);
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('avatar_image', file);
            const response = await fetch(uploadUrl, { method: 'POST', headers: buildAuthHeaders(), body: formData });
            // fetch only rejects on network errors, so a non-2xx (e.g. 413 too-large, 422) would
            // otherwise fall through with no avatar url and no signal — surface it as an error instead.
            if (!response.ok) throw new Error(`upload_avatar responded with status ${response.status}`);
            const data = (await response.json()) as UploadAvatarResponse;
            if (data.contents?.avatar?.url) setNewValueOnModel(data.contents.avatar.url);
        } catch (error) {
            // Log but intentionally don't toast, mirroring the old upload-avatar directive: the server
            // logs its own error and an avatar-upload failure isn't worth interrupting the user with an
            // API-error toast.
            ErrorLogService.notify('Failed to upload avatar', error);
        } finally {
            setLoading(false);
        }
    };

    // Mobile (Cordova) upload: a data URL from the Capacitor camera, sent as JSON.
    const getAndUploadPhoto = async (source: CameraSource) => {
        const permissionStatus = await requestPermissions(source);
        if (permissionStatus === 'denied') {
            setShowAccessTip(true);
            return;
        }
        setShowAccessTip(false);

        setLoading(true);
        try {
            const { dataUrl } = await getPhoto(source);
            const response = await fetch(uploadUrl, {
                method: 'POST',
                headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ avatar_image: dataUrl }),
            });
            const data = (await response.json()) as UploadAvatarResponse;
            if (data.contents?.avatar?.url) setNewValueOnModel(data.contents.avatar.url);
        } catch {
            // The likeliest cause is the user cancelling the photo picker — nothing actionable.
        } finally {
            setLoading(false);
        }
    };

    return { avatarUrl, loading, showAccessTip, oversized, uploadFile, getAndUploadPhoto };
}
